import React, { Fragment, useState } from "react";
import useAuth from "src/hooks/useAuth";
import "./navbar.css";
import { useHistory } from "react-router-dom";
import logo from "src/assets/logo.png";
import { LogoutIcon, XIcon } from "@heroicons/react/outline";
import { useSelector, useDispatch } from "react-redux";
import {
  currentOfficeSelector,
  officesSelector,
  setOffice,
} from "src/slices/office";
import Menu from "./components/Menu";
import PublicMenu from "./components/PublicMenu";
import PublicPopup from "src/components/PublicPopup";

const NavBar = ({
  showMobileNav,
  onCloseMobileNav,
  onBellClicked,
  notiUnreadNum,
}) => {
  const { logout, user } = useAuth();
  const history = useHistory();
  const currentOffice = useSelector(currentOfficeSelector);
  const currentOfficeId = currentOffice.id;
  const role = currentOffice.attributes.role;
  const tier = currentOffice.attributes.tier;
  const isForecasts = currentOffice.attributes.forecasts;

  const [isOpenModal, setIsOpenModal] = useState(false);

  const connectEmail = useSelector(
    state => state.office.currentOffice.attributes.connect_email
  );

  // Office selector for mobile drawer
  const dispatch = useDispatch();
  const offices = useSelector(officesSelector);

  const changeOffice = officeId => {
    if (officeId < 0) {
      history.push("/office/setup");
      onCloseMobileNav();
      return;
    }

    if (officeId != currentOffice.id) {
      dispatch(setOffice(offices.find(office => office.id === officeId)));
      history.push("/posts");
      onCloseMobileNav();
    }
  };

  const handleLogout = () => {
    logout().then(() => {
      console.log("Log out!");
    });
  };

  const onPublicMenuClick = () => {
    setIsOpenModal(true);
  };

  const onPublicOffices = () => {
    if (user) history.push("/public_offices");
    else history.push("/discover");
  };

  const onPublicOfficePosts = () => {
    // if (user) history.push("/public_offices");
    history.push(`/public_offices/${currentOfficeId}/posts`);
  };

  let navigationBar;
  if (user) {
    navigationBar = (
      <Fragment>
        <div className="flex flex-col">
          <div className="mb-4 px-4 flex justify-between items-center lg:justify-start">
            <img src={logo} width="120" />
            <button
              className="lg:hidden p-1 hover:bg-gray-100 rounded-md -mr-2"
              onClick={onCloseMobileNav}
              aria-label="Close menu"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Mobile-only Office Selector */}
          <div className="lg:hidden mb-4 px-4 pb-4 border-b border-gray-200">
            {offices.length === 0 ? (
              <div className="text-sm font-medium text-gray-700">
                {currentOffice?.attributes?.name}
              </div>
            ) : (
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                onChange={e => changeOffice(e.target.value)}
                value={currentOffice.id === null ? "" : currentOffice.id}
              >
                {offices.map(office => (
                  <option key={office.id} value={office.id}>
                    {office.attributes.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          {role !== "subscriber" && (
            <div>
              {/* {connectEmail && (
                <Fragment>
                  <Menu title="Home" />
                  {tier && tier !== "free" && <Menu title="Emails" />}
                </Fragment>
              )} */}
              <Menu title="Feed" onMenuClick={onCloseMobileNav} />
              {tier && tier !== "free" && (
                <Fragment>
                  <Menu title="Messages" onMenuClick={onCloseMobileNav} />
                  <Menu title="Tasks" onMenuClick={onCloseMobileNav} />
                </Fragment>
              )}
              {connectEmail && (
                <Fragment>
                  <Menu title="Workflows" onMenuClick={onCloseMobileNav} />
                  <Menu title="Templates" onMenuClick={onCloseMobileNav} />
                </Fragment>
              )}
              <Menu title="Posts" onMenuClick={onCloseMobileNav} />
              <Menu title="Explore" onMenuClick={onCloseMobileNav} />
              <Menu title="People" onMenuClick={onCloseMobileNav} />
              <Menu title="Bills" onMenuClick={onCloseMobileNav} />
              <Menu title="Topics" onMenuClick={onCloseMobileNav} />
              <Menu title="Subtopics" onMenuClick={onCloseMobileNav} />
              <Menu title="Contacts" onMenuClick={onCloseMobileNav} />
              {isForecasts && (
                <Menu title="Forecasts" onMenuClick={onCloseMobileNav} />
              )}
            </div>
          )}
          {role === "subscriber" && (
            <Fragment>
              <Menu title="Posts" onMenuClick={onCloseMobileNav} />
              <Menu title="Explore" onMenuClick={onCloseMobileNav} />
              <Menu title="People" onMenuClick={onCloseMobileNav} />
              <Menu title="Bills" onMenuClick={onCloseMobileNav} />
              <Menu title="Topics" onMenuClick={onCloseMobileNav} />
              <Menu title="Subtopics" onMenuClick={onCloseMobileNav} />
              <Menu title="Contacts" onMenuClick={onCloseMobileNav} />
            </Fragment>
          )}
        </div>
        <div>
          {/* Mobile-only: Notifications and Account */}
          <div className="lg:hidden border-t border-gray-200 pt-2 mb-2">
            <div
              className="px-4 py-2 cursor-pointer hover:bg-purple-100 rounded-r-sm flex items-center justify-between"
              onClick={() => {
                onBellClicked();
                onCloseMobileNav();
              }}
            >
              <div className="flex items-center relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                {notiUnreadNum > 0 && (
                  <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[18px]">
                    {notiUnreadNum}
                  </span>
                )}
              </div>
            </div>
            <Menu title="Account" onMenuClick={onCloseMobileNav} />
          </div>
          <Menu title="Office" onMenuClick={onCloseMobileNav} />
          <Menu title="Notifications" onMenuClick={onCloseMobileNav} />
          <Menu title="Discover" onMenuClick={onCloseMobileNav} />
          <div className="hidden lg:block">
            <Menu title="Account" onMenuClick={onCloseMobileNav} />
          </div>
          <div
            className="px-4 py-1 cursor-pointer hover:bg-purple-100 rounded-r-sm flex items-center space-x-1"
            onClick={handleLogout}
          >
            <div className="h-4 w-4">
              <LogoutIcon />
            </div>
            <span>Logout</span>
          </div>
        </div>
      </Fragment>
    );
  } else {
    navigationBar = (
      <Fragment>
        <div className="flex flex-col">
          <div className="mb-8 px-4">
            <img src={logo} width="120" />
          </div>
          <PublicMenu title="Data" onMenuClick={onPublicMenuClick} />
          <PublicMenu title="Contacts" onMenuClick={onPublicMenuClick} />
          {currentOfficeId && (
            <PublicMenu title="Posts" onMenuClick={onPublicOfficePosts} />
          )}
          <PublicMenu title="Office" onMenuClick={onPublicMenuClick} />
          <PublicMenu title="Notifications" onMenuClick={onPublicMenuClick} />
        </div>
        <div className="mb-10">
          <PublicMenu title="Discover" onMenuClick={onPublicOffices} />
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {/* Mobile overlay */}
      {showMobileNav && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onCloseMobileNav}
        />
      )}

      {/* Sidebar - hidden on mobile, shown on desktop, or shown as drawer on mobile when open */}
      <div
        className={`
        w-40 flex flex-col justify-between items-start h-full fixed py-6 bg-white border-r-1 border-gray-100 z-50
        transform transition-transform duration-300 ease-in-out
        ${showMobileNav ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        {navigationBar}
      </div>
      <PublicPopup isOpen={isOpenModal} onClose={() => setIsOpenModal(false)} />
    </Fragment>
  );
};

export default NavBar;
